import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Shield, FileText, ClipboardList, FileSearch, LogOut, User, Moon, Sun, Bell, HelpCircle } from 'lucide-react';
import { GuidedTour, useTour } from '../components/ui/guided-tour';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { unreadCount, notifications, markAsRead } = useNotifications();
  const navigate = useNavigate();
  const { isOpen: isTourOpen, startTour, closeTour, autoStart } = useTour('dashboard_tour');
  const [showNotifications, setShowNotifications] = useState(false);

  // Auto-start tour for new users
  useEffect(() => {
    autoStart();
  }, [autoStart]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tourSteps = [
    {
      target: '[data-testid="menu-item-0"]',
      title: 'Nueva Solicitud de Garantía',
      content: 'Aquí puedes iniciar una nueva solicitud de garantía para tu producto. Solo necesitas unos minutos.',
      position: 'bottom'
    },
    {
      target: '[data-testid="menu-item-1"]',
      title: 'Consultar Garantías',
      content: 'Revisa el estado de todas tus solicitudes de garantía en tiempo real.',
      position: 'bottom'
    },
    {
      target: '[data-testid="menu-item-2"]',
      title: 'Solicitar Factura',
      content: 'Necesitas una factura? Solicítala aquí con tus datos fiscales.',
      position: 'bottom'
    },
    {
      target: '[data-testid="theme-toggle"]',
      title: 'Modo Oscuro',
      content: 'Puedes cambiar entre modo claro y oscuro según tu preferencia.',
      position: 'bottom'
    }
  ];

  const menuItems = [
    {
      title: t('dashboard.warranty.new'),
      description: 'Inicia una nueva solicitud de garantía para tu producto',
      icon: Shield,
      link: '/warranty/new',
      color: 'bg-[#0047FF]'
    },
    {
      title: t('dashboard.warranty.check'),
      description: 'Consulta el estado de tus solicitudes de garantía',
      icon: ClipboardList,
      link: '/warranty',
      color: 'bg-[#8B5CF6]'
    },
    {
      title: t('dashboard.invoice.new'),
      description: 'Solicita una factura para tu compra',
      icon: FileText,
      link: '/invoice/new',
      color: 'bg-[#10B981]'
    },
    {
      title: t('dashboard.invoice.check'),
      description: 'Consulta el estado de tus solicitudes de factura',
      icon: FileSearch,
      link: '/invoice',
      color: 'bg-[#F59E0B]'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-gray-900 transition-colors" data-testid="dashboard-page">
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        Saltar al contenido principal
      </a>

      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-[#E5E7EB] dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#0047FF] rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="font-heading font-bold text-xl tracking-tight dark:text-white">Arreglatelo</span>
            </Link>

            <div className="flex items-center gap-2 sm:gap-4">
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-[#52525B] dark:text-gray-300"
                data-testid="theme-toggle"
                aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* Notifications */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="text-[#52525B] dark:text-gray-300 relative"
                  aria-label={`Notificaciones (${unreadCount} sin leer)`}
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>

                {/* Notifications dropdown */}
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute right-0 top-12 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-[#E5E7EB] dark:border-gray-700 overflow-hidden z-50"
                  >
                    <div className="p-4 border-b border-[#E5E7EB] dark:border-gray-700">
                      <h3 className="font-semibold dark:text-white">Notificaciones</h3>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length > 0 ? (
                        notifications.slice(0, 5).map((notif) => (
                          <button
                            key={notif.notification_id}
                            onClick={() => {
                              markAsRead(notif.notification_id);
                              if (notif.url) navigate(notif.url);
                              setShowNotifications(false);
                            }}
                            className={`w-full p-4 text-left hover:bg-[#F5F5F7] dark:hover:bg-gray-700 transition-colors ${
                              !notif.read ? 'bg-[#0047FF]/5' : ''
                            }`}
                          >
                            <p className="font-medium text-sm dark:text-white">{notif.title}</p>
                            <p className="text-xs text-[#52525B] dark:text-gray-400 mt-1">{notif.body}</p>
                          </button>
                        ))
                      ) : (
                        <p className="p-4 text-sm text-[#A1A1AA] text-center">No hay notificaciones</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Help/Tour button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={startTour}
                className="text-[#52525B] dark:text-gray-300 hidden sm:flex"
                aria-label="Iniciar tour guiado"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>

              {/* User info */}
              <div className="flex items-center gap-2 text-sm">
                <div className="w-8 h-8 bg-[#F5F5F7] dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-[#52525B] dark:text-gray-300" />
                </div>
                <span className="font-medium hidden sm:block dark:text-white">{user?.name || user?.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-[#52525B] dark:text-gray-300"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="text-3xl font-bold mb-2 dark:text-white">{t('dashboard.title')}</h1>
          <p className="text-[#52525B] dark:text-gray-400">
            {t('dashboard.welcome')}, <span className="font-semibold">{user?.name || user?.email?.split('@')[0]}</span>
          </p>
        </motion.div>

        {/* Menu Grid */}
        <div className="grid sm:grid-cols-2 gap-6">
          {menuItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={item.link} data-testid={`menu-item-${index}`}>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-[#E5E7EB] dark:border-gray-700 p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-200 h-full">
                  <div className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2 dark:text-white">{item.title}</h3>
                  <p className="text-[#52525B] dark:text-gray-400 text-sm">{item.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Guided Tour */}
      <GuidedTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={closeTour}
        onComplete={() => {}}
        storageKey="dashboard_tour"
      />
    </div>
  );
};

export default Dashboard;
